import React, { useState, useRef } from 'react';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { XMLParser } from 'fast-xml-parser';
import { processData } from './utils/FormatIdentificator';
import ContactSection from './ContactSection';

const PageConverter = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [downloadLink, setDownloadLink] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const [fileUploaded, setFileUploaded] = useState(false);
    const [processingError, setProcessingError] = useState('');
    const [parsedData, setParsedData] = useState(null);
    const [displayJson, setDisplayJson] = useState(true);
    const [exportEmbeddedDoc, setExportEmbeddedDoc] = useState(true);
    const downloadFilename = useRef('default.pdf')

    const handleFileChange = (event) => {
        const uploadedFile = event.target.files[0];
        if (uploadedFile && /\.(xml|XML)$/.test(uploadedFile.name)) {
            setFile(uploadedFile);
            downloadFilename.current = replaceSuffix(uploadedFile.name);
            setDownloadLink(''); // Hide the download button when a new file is uploaded
            setFileUploaded(false);
            setProcessingError('');
        } else {
            setFile(null);
            setProcessingError('Only XML file format is supported');
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = () => {
        setDragActive(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragActive(false);
        const uploadedFile = event.dataTransfer.files[0];
        if (uploadedFile && /\.(xml|XML)$/.test(uploadedFile.name)) {
            downloadFilename.current = replaceSuffix(uploadedFile.name);
            setFile(uploadedFile);
            setDownloadLink(''); // Hide the download button when a new file is uploaded
            setFileUploaded(true); // Set file uploaded state to true
            setProcessingError('');
        } else {
            setFile(null);
            setProcessingError('Only XML file format is supported');
        }
    };

    const handleProcessing = () => {
        setIsProcessing(true);
        setProcessingError(false);
        const reader = new FileReader();

        reader.onload = (e) => {
            const xmlContent = e.target.result;
            try {

                const options = {
                    ignoreAttributes: false,
                    attributeNamePrefix: "@_"
                };

                const parser = new XMLParser(options);
                // const parser = new XMLParser();
                const jsonObj = parser.parse(xmlContent);
                setParsedData(jsonObj);

                // Call the processing function and create a blob URL for the PDF
                const [pdfBlob, errorMsg] = processData(jsonObj, exportEmbeddedDoc);
                const pdfUrl = URL.createObjectURL(pdfBlob);
                setDownloadLink(pdfUrl);
                setIsProcessing(false);
                setProcessingError(errorMsg);
            } catch (error) {
                console.log(error);
                setProcessingError('There was an error processing your file. Please try again.');
                setParsedData(null);
                setIsProcessing(false);
            }
        };

        if (file) {
            reader.readAsText(file);
        }
    };


    const replaceSuffix = (filename) => {
        // replace filename sufix by .pdf
        return filename.replace(/\.[^/.]+$/, '') + '.pdf';
    }

    return (
        <div className="container mt-5">
            <main>
                <h1 className="text-center">Free E-Invoice Converter</h1>
                <p className="text-center">Extract embedded PDF file from the Peppol document or generate a human-readable version in PDF format if the embedded file is missing.</p>

                <div className="mt-4">
                    <h2 className="h5">Supported E-Invoice Formats</h2>
                    <ul>
                        <li>Peppol</li>
                        <li>UBL</li>
                    </ul>
                </div>

                <div className="card p-4">
                    <div
                        className={`mb-3 ${dragActive ? 'border border-primary' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {!fileUploaded ? (
                            <>
                                <label htmlFor="formFile" className="form-label">Upload your e-invoice XML file</label>
                                <input className="form-control" type="file" id="formFile" onChange={handleFileChange} disabled={isProcessing} accept=".xml" />
                                <div className="text-center mt-2">or drag and drop your file here</div>
                            </>
                        ) : (
                            <div className="text-center mt-2">
                                <FaCheckCircle color="green" size="2em" />
                                <p>File uploaded successfully!</p>
                            </div>
                        )}
                    </div>
                    <button className="btn btn-primary" onClick={handleProcessing} disabled={!file || isProcessing}>
                        {isProcessing ? 'Processing...' : 'Process'}
                    </button>
                    {isProcessing && (
                        <div className="progress mt-3">
                            <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: '100%' }}></div>
                        </div>
                    )}
                    {!isProcessing && !processingError && downloadLink && (
                        <div className="mt-3 text-center">
                            <FaCheckCircle color="green" size="2em" />
                            <p>Processing finished successfully!</p>
                            <a href={downloadLink} className="btn btn-success" download={downloadFilename.current}>Download Converted File</a>
                        </div>
                    )}
                    {!isProcessing && processingError && (
                        <div className="mt-3 text-center">
                            <FaExclamationCircle color="red" size="2em" />
                            <p style={{ color: 'red' }}>{processingError}</p>
                        </div>
                    )}
                </div>

                <div className="mt-4">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={displayJson}
                            id="displayJson"
                            onChange={() => setDisplayJson(!displayJson)}
                        />
                        <label className="form-check-label" htmlFor="displayJson">
                            Display JSON
                        </label>
                    </div>

                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={exportEmbeddedDoc}
                            id="exportEmbeddedDoc"
                            onChange={() => setExportEmbeddedDoc(!exportEmbeddedDoc)}
                        />
                        <label className="form-check-label" htmlFor="displayJson">
                            Export embedded document if possible
                        </label>
                    </div>

                </div>

                {displayJson && parsedData && (
                    <div className="mt-3">
                        <h2>Parsed XML Data:</h2>
                        <pre>{JSON.stringify(parsedData, null, 2)}</pre>
                    </div>
                )}


            </main>
            <div className='mt-4'>
                <ContactSection />
            </div>

            <div className="alert alert-warning" role="alert">
            <strong>Security</strong> - All processing is done in the internet browser on your local PC only. Your invoices are not saved on our servers nor sent via the internet. Your invoices and sensitive information are safe.
            </div>

        </div>
    );
};

export default PageConverter;
